import { observer } from 'mobx-react-lite'
import { useRef, useState, useEffect, useMemo } from 'react'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Button } from 'blaze-ui/Button/Button'
import { Checkbox } from 'blaze-ui/Checkbox/Checkbox'
import { TextInputField } from 'components/Form/TextInputField'
import Styles from '../../../BlogMetadata.module.scss'

type CategoryDropdownProps = {
  availableCategories: string[]
  selectedCategories: string[]
  onCategoriesChange: (categories: string[]) => void
  onDropdownClose?: (selectedCategories: string[], availableCategories: string[]) => void
  allowAddingCategories?: boolean
}

export const CategoryDropdown = observer(
  ({
    availableCategories,
    selectedCategories,
    onCategoriesChange,
    onDropdownClose,
    allowAddingCategories = true,
  }: CategoryDropdownProps) => {
    const [popoverWidth] = useState<number | undefined>(undefined)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const hasAutoSelected = useRef(false)
    const [newCategory, setNewCategory] = useState<string>('')

    useEffect(() => {
      if ((!hasAutoSelected.current && availableCategories.length === 0) || selectedCategories.length === 0) {
        hasAutoSelected.current = true
        onCategoriesChange(['Uncategorized'])
      }
    }, [availableCategories, selectedCategories, onCategoriesChange])

    const handleCategoryToggle = (category: string) => {
      if (selectedCategories.includes(category)) {
        onCategoriesChange(selectedCategories.filter((cat) => cat !== category))
      } else {
        onCategoriesChange([...selectedCategories, category])
      }
    }

    const selectedText = useMemo(() => {
      if (selectedCategories.length > 0) {
        return selectedCategories.join(', ')
      }
      return 'A category is required'
    }, [selectedCategories])

    const addNewCategory = (categoryName: string) => {
      if (!categoryName.trim()) return

      const trimmedName = categoryName.trim()

      // Check if category already exists (case-insensitive)
      const existingCategory = availableCategories.find((cat) => cat.toLowerCase() === trimmedName.toLowerCase())

      if (existingCategory) {
        // If category exists but not selected, select it
        if (!selectedCategories.includes(existingCategory)) {
          onCategoriesChange([...selectedCategories, existingCategory])
        }
        return
      }

      // Add new category to selected categories (it will be auto-selected)
      onCategoriesChange([...selectedCategories, trimmedName])

      setNewCategory('')
    }

    const handleNewCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewCategory(e.target.value)
    }

    const handleNewCategoryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        addNewCategory(newCategory)
      }
    }

    const handleNewCategoryBlur = () => {
      if (newCategory.trim()) {
        addNewCategory(newCategory)
      }
    }

    const handleDropdownClose = () => {
      // Get all categories (existing + any newly selected ones that aren't in availableCategories)
      // Only include newly added categories if adding is allowed
      const newlyAddedCategories = allowAddingCategories
        ? selectedCategories.filter((cat) => !availableCategories.includes(cat))
        : []
      const updatedAvailableCategories = [...availableCategories, ...newlyAddedCategories]

      // Call parent callback to persist changes
      onDropdownClose?.(selectedCategories, updatedAvailableCategories)
    }

    // Get all categories to display (available + any selected that aren't in available yet)
    const allCategoriesToShow = () => {
      const baseCategories = availableCategories.length === 0 ? ['Uncategorized'] : availableCategories
      const additionalSelected = selectedCategories.filter((cat) => !baseCategories.includes(cat))
      return [...baseCategories, ...additionalSelected]
    }

    return (
      <DropdownTrigger
        __UNSAFE_popoverStyle={popoverWidth ? { width: popoverWidth } : undefined}
        onClose={handleDropdownClose}
      >
        <Button
          variant="secondary"
          withChevron="down"
          size="lg"
          ref={triggerRef}
          fullWidth
          className={Styles.categoryDropdownButton}
        >
          <span className={Styles.categoryText}>{selectedText}</span>
        </Button>
        <CategoryDropDownContent
          allCategoriesToShow={allCategoriesToShow}
          selectedCategories={selectedCategories}
          handleCategoryToggle={handleCategoryToggle}
          handleNewCategoryChange={handleNewCategoryChange}
          handleNewCategoryKeyDown={handleNewCategoryKeyDown}
          handleNewCategoryBlur={handleNewCategoryBlur}
          addNewCategory={addNewCategory}
          newCategory={newCategory}
          allowAddingCategories={allowAddingCategories}
        />
      </DropdownTrigger>
    )
  },
)

type CategoryDropDownContentProps = {
  allCategoriesToShow: () => string[]
  selectedCategories: string[]
  handleCategoryToggle: (category: string) => void
  handleNewCategoryChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleNewCategoryKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleNewCategoryBlur: () => void
  addNewCategory: (categoryName: string) => void
  newCategory: string
  allowAddingCategories: boolean
}

const CategoryDropDownContent = observer(
  ({
    allCategoriesToShow,
    selectedCategories,
    handleCategoryToggle,
    handleNewCategoryChange,
    handleNewCategoryKeyDown,
    handleNewCategoryBlur,
    addNewCategory,
    newCategory,
    allowAddingCategories,
  }: CategoryDropDownContentProps) => {
    return (
      <div className={Styles.categoryDropdownContent}>
        {allCategoriesToShow().map((category: string) => (
          <div key={category} className={Styles.categoryRow}>
            <Checkbox
              isSelected={selectedCategories.includes(category)}
              color="var(--selected-fill)"
              onChange={() => handleCategoryToggle(category)}
            >
              {category}
            </Checkbox>
          </div>
        ))}

        {allowAddingCategories && (
          <div className={Styles.inputRow}>
            <div className={Styles.inputWrapper}>
              <TextInputField
                value={newCategory}
                placeholder="Add new category"
                onChange={handleNewCategoryChange}
                onKeyDown={handleNewCategoryKeyDown}
                onBlur={handleNewCategoryBlur}
                size="lg"
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              className={Styles.addButton}
              onClick={() => addNewCategory(newCategory)}
              isDisabled={!newCategory.trim()}
            >
              Add
            </Button>
          </div>
        )}
      </div>
    )
  },
)
